import {
  Alert,
  Button,
  Center,
  Checkbox,
  FileInput,
  Group,
  Loader,
  MultiSelect,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParticipationSubmissionError, submitParticipationRequest } from '../api/participation';
import PageShell from '../components/PageShell';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import type { FieldSchema } from '../types/schema';
import NotFoundPage from './NotFoundPage';
import {IconAlertOctagonFilled} from '@tabler/icons-react';

function validateParticipationFields(fields: FieldSchema[], values: Record<string, unknown>) {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const rawValue = values[field.id];
    const value = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (field.required && !value) {
      errors[field.id] = 'This field is required.';
      continue;
    }
    if (!value) continue;
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field.id] = 'Enter a valid email address.';
      continue;
    }
    if (field.validation?.maxLength && value.length > field.validation.maxLength) {
      errors[field.id] = `Enter no more than ${field.validation.maxLength} characters.`;
      continue;
    }
    if (field.validation?.pattern && !new RegExp(field.validation.pattern).test(value)) {
      errors[field.id] = field.validation.patternMessage ?? 'Enter a valid value.';
    }
  }
  return errors;
}

interface ParticipationFieldProps {
  disabled: boolean;
  error?: string;
  field: FieldSchema;
  onChange: (value: unknown) => void;
  value: unknown;
}

function ParticipationField({ disabled, error, field, onChange, value }: ParticipationFieldProps) {
  const commonProps = {
    disabled,
    error,
    label: field.label,
    description: field.helpText,
    placeholder: field.placeholder,
    size: 'md' as const,
    withAsterisk: field.required,
  };

  switch (field.type) {
    case 'hidden':
      return null;
    case 'textarea':
      return <Textarea {...commonProps} value={String(value ?? '')} onChange={(event) => onChange(event.currentTarget.value)} />;
    case 'select':
      return <Select {...commonProps} data={field.options ?? []} value={String(value ?? '') || null} onChange={(nextValue) => onChange(nextValue ?? '')} />;
    case 'multiselect':
      return <MultiSelect {...commonProps} data={field.options ?? []} value={Array.isArray(value) ? value as string[] : []} onChange={onChange} />;
    case 'radio':
      return (
        <Radio.Group label={field.label} description={field.helpText} withAsterisk={field.required} value={String(value ?? '')} onChange={onChange} error={error}>
          <Stack gap="xs" mt="xs">
            {(field.options ?? []).map((option) => (
              <Radio key={option.value} value={option.value} label={option.label} disabled={disabled} size="md" />
            ))}
          </Stack>
        </Radio.Group>
      );
    case 'checkbox':
      return (
        <Checkbox
          label={field.checkboxLabel ?? field.label}
          description={field.helpText}
          checked={Boolean(value)}
          disabled={disabled}
          error={error}
          size="md"
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
      );
    case 'file':
      return <FileInput {...commonProps} value={value instanceof File ? value : null} onChange={onChange} />;
    default:
      return (
        <TextInput
          {...commonProps}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.currentTarget.value)}
          type={field.type === 'address' ? 'text' : field.type}
        />
      );
  }
}

export default function ParticipationPage() {
  const { data: config, isLoading, error } = useResolvedProgram();
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>();
  const [requiredFieldsIncomplete, setRequiredFieldsIncomplete] = useState(false);
  const validationAlertRef = useRef<HTMLDivElement>(null);
  const failureMessage = config?.voucherParticipationRequest?.pages.failure.message
    ?? 'Unable to submit the participation request. Please try again.';
  const submission = useMutation({
    mutationFn: () => submitParticipationRequest(values),
    onSuccess: () => navigate('/voucher-participation-request/success'),
    onError: (submissionError) => {
      if (submissionError instanceof ParticipationSubmissionError) {
        setFieldErrors(submissionError.fieldErrors ?? {});
        setSubmitError(submissionError.fieldErrors
          ? undefined
          : submissionError.code === 'participation_request_notification_failed'
            ? config?.voucherParticipationRequest?.pages.notificationFailure.message
            : failureMessage);
        return;
      }
      setSubmitError(failureMessage);
    },
  });

  useEffect(() => {
    if (requiredFieldsIncomplete) {
      validationAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [requiredFieldsIncomplete]);

  const updateValue = (fieldID: string, value: unknown) => {
    setValues((current) => ({ ...current, [fieldID]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[fieldID];
      return next;
    });
    setSubmitError(undefined);
  };

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !config?.voucherParticipationRequest) {
    return <NotFoundPage />;
  }

  const participationConfig = config.voucherParticipationRequest;
  const sections = participationConfig.pages.form?.[0]?.sections ?? [];
	const requiredFieldsNote = participationConfig.pages.requiredFieldsNote;
	const fields = sections.flatMap((section) => section.fields);

  return (
    <PageShell
      config={config}
      containerSize="lg"
      headerEyebrow={config.theme.displayName}
      headerTitle={participationConfig.pages.title}
      headerSubtitle={participationConfig.pages.subtitle}
      headerSubtitleNote={requiredFieldsNote ? <>
        {requiredFieldsNote.startsWith('*') && <Text component="span" c="red">*</Text>}
        {requiredFieldsNote.startsWith('*') ? requiredFieldsNote.slice(1) : requiredFieldsNote}
      </> : undefined}
      showFooter
      contentSurface={config.theme.contentSurface === true}
    >
      <Stack
        renderRoot={(props) => <form {...props} noValidate />}
        gap="lg"
        mih={320}
        aria-label="Participation Form"
        onSubmit={(event) => {
          event.preventDefault();
      const validationErrors = validateParticipationFields(fields, values);
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setRequiredFieldsIncomplete(true);
        return;
      }
          setRequiredFieldsIncomplete(false);
          submission.mutate();
        }}
      >
        <div ref={validationAlertRef}>
          {requiredFieldsIncomplete && (
            <Alert
              color="red"
              icon={<IconAlertOctagonFilled />}
              title={<Text c="#80170c" fw={700}>Please complete all required fields</Text>}
              variant="light"
            >
              <Text c="#80170c">
                The form cannot be submitted until every field marked with * has been completed. Missing fields are highlighted below.
              </Text>
            </Alert>
          )}
        </div>
        {sections.map((section) => {
          const columns = section.columns ?? 1;

          return (
            <Paper key={section.id} withBorder p="xl" className="portal-panel">
              <Title order={3} mb={section.description ? 'xs' : 'md'} className="portal-section-title">{section.title}</Title>
              {section.description && <Text size="sm" c="dimmed" mb="md">{section.description}</Text>}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  gap: 'var(--mantine-spacing-md)',
                }}
              >
                {section.fields.map((field) => (
                  <div
                    key={field.id}
                    style={field.span ? { gridColumn: `span ${Math.min(field.span, columns)}` } : undefined}
                  >
                    <ParticipationField
                      disabled={submission.isPending}
                      error={fieldErrors[field.id]}
                      field={field}
                      value={values[field.id]}
                      onChange={(value) => updateValue(field.id, value)}
                    />
                  </div>
                ))}
              </div>
            </Paper>
          );
        })}
        {submitError && <Alert color="red">{submitError}</Alert>}
        <Group justify="flex-end">
          <Button
            type="submit"
            size="md"
            className="portal-primary-button portal-accent-button"
            loading={submission.isPending}
          >
            Submit for Review
          </Button>
        </Group>
      </Stack>
    </PageShell>
  );
}